import React, { useEffect, useState } from 'react';
import { Container, Table, Alert, Spinner, Tabs, Tab } from 'react-bootstrap';
import { fetchWithAuth } from '../lib/supabaseClient';

function excelDateToJSDate(serial: number) {
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);
  return date_info.toLocaleDateString('en-GB');
}

function excelTimeToString(time: number) {
  if (typeof time !== 'number') return time;
  const totalMinutes = Math.round(time * 24 * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
}

const Schedule: React.FC = () => {
  const [fixtures, setFixtures] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('future');
  const [linoTeams, setLinoTeams] = useState<string[]>([]);

  useEffect(() => {
    fetchWithAuth('/.netlify/functions/schedule')
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        const processed = data.schedule.map((row: any) => {
          let newRow: any = {};
          Object.entries(row).forEach(([key, value]) => {
            const cleanKey = key.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ').trim();
            newRow[cleanKey] = value;
          });
          if (newRow.Date && typeof newRow.Date === 'number') {
            newRow.Date = excelDateToJSDate(newRow.Date);
          }
          if (newRow['Kick Off'] && typeof newRow['Kick Off'] === 'number') {
            newRow['Kick Off'] = excelTimeToString(newRow['Kick Off']);
          }
          return newRow;
        });

        processed.sort((a: any, b: any) => {
          const parseDate = (d: string) => {
            const [day, month, year] = d.split('/');
            return new Date(`${year}-${month}-${day}`);
          };
          return parseDate(b.Date).getTime() - parseDate(a.Date).getTime();
        });
        setFixtures(processed);
        setLinoTeams(data.linoTeams);
        setLoading(false);
      })
      .catch(() => {
        setError('Could not load schedule file.');
        setLoading(false);
      });
  }, []);

  const groupFixturesByDate = (fixturesList: any[]) => {
    const grouped: { [date: string]: any[] } = {};
    fixturesList.forEach(fixture => {
      if (!grouped[fixture.Date]) grouped[fixture.Date] = [];
      grouped[fixture.Date].push(fixture);
    });
    return grouped;
  };

  const getFutureFixtures = () => {
    const now = new Date();
    return fixtures.filter(fixture => {
      if (!fixture.Date) return false;
      const [day, month, year] = fixture.Date.split('/');
      const fixtureDate = new Date(`${year}-${month}-${day}`);
      return fixtureDate > now;
    });
  };

  const getPreviousAndNextFixtures = () => {
    const now = new Date();
    return fixtures.filter(fixture => {
      if (!fixture.Date) return false;
      const [day, month, year] = fixture.Date.split('/');
      const fixtureDate = new Date(`${year}-${month}-${day}`);
      return fixtureDate < now;
    });
  }

  const sortFixturesByDate = (fixturesList: any[], ascending: boolean) => {
    return [...fixturesList].sort((a, b) => {
      const parseDate = (d: string) => {
        const [day, month, year] = d.split('/');
        return new Date(`${year}-${month}-${day}`);
      };
      return ascending
        ? parseDate(a.Date).getTime() - parseDate(b.Date).getTime()
        : parseDate(b.Date).getTime() - parseDate(a.Date).getTime();
    });
  };

  const desiredOrder = [
    "Home",
    "Away",
    "L/C/F",
    "Defib & Signs",
    "Pitch",
    "Kick Off",
    "Referee",
    "Assistant"
  ];

  const columns = fixtures.length > 0
    ? Array.from(
        new Set(
          fixtures.flatMap(row => Object.keys(row))
        )
      ).filter(key => key !== 'Date')
  : [];

  const orderedColumns = [
    ...desiredOrder.filter(col => columns.includes(col)),
    ...columns.filter(col => !desiredOrder.includes(col))
  ];

  return (
    <Container className="p-3 my-4">
    <h2 className="mb-4">2025-26 Fixtures Schedule</h2>
    {loading && <Spinner animation="border" />}
    {error && <Alert variant="danger">{error}</Alert>}
    {!loading && !error && (
      <Tabs
        activeKey={activeTab}
        onSelect={k => setActiveTab(k || 'future')}
        className="mb-4"
      >
        <Tab eventKey="future" title="Future Fixtures">
          {getFutureFixtures().length > 0 ? (
            <>
              {Object.entries(
                groupFixturesByDate(
                  sortFixturesByDate(getFutureFixtures(), true)
                )
              ).map(([date, group]) => (
                <div key={date} className="mb-5">
                  <h4 className="mb-3 text-center" style={{ color: '#254E97' }}>{date}</h4>
                  <div className="table-responsive">
                    <Table striped bordered hover responsive className="responsive-table custom-fixture-table mb-4">
                    <thead>
                      <tr>
                        {orderedColumns.map((key) => (
                          <th key={key}>{key}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                    {group.map((row, idx) => (
                        <tr key={idx}>
                        {orderedColumns.map((key, i) => {
                            const isLinoTeam = key === 'Home' && linoTeams.includes(row[key]);
                            let value = row[key] ?? '';
                            if (key === 'Home' && typeof value === 'string') {
                              value = value.toUpperCase();
                            }
                            return (
                            <td
                                key={i}
                                style={isLinoTeam ? { backgroundColor: '#254E97', color: 'white', fontWeight: 'bold' } : {}}
                            >
                                {value}
                            </td>
                            );
                        })}
                        </tr>
                    ))}
                    </tbody>
                  </Table>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <Alert variant="info">No future fixtures found.</Alert>
          )}
        </Tab>
        <Tab eventKey="fixtures" title="Past Fixtures">
          {fixtures.length > 0 ? (
            <>
              {Object.entries(
                groupFixturesByDate(
                  sortFixturesByDate(getPreviousAndNextFixtures(), false)
                )
              ).map(([date, group]) => (
                <div key={date} className="mb-5">
                  <h4 className="mb-3 text-center" style={{ color: '#254E97' }}>{date}</h4>
                  <div className="table-responsive">

                    <Table striped bordered hover responsive className="responsive-table custom-fixture-table mb-4">
                        <thead>
                        <tr>
                            {orderedColumns.map((key) => (
                            <th key={key}>{key}</th>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        {group.map((row, idx) => (
                            <tr key={idx}>
                              {orderedColumns.map((key, i) => {
                                  const isLinoTeam = key === 'Home' && linoTeams.includes(row[key]);
                                  let value = row[key] ?? '';
                                  if (key === 'Home' && typeof value === 'string') {
                                    value = value.toUpperCase();
                                  }
                                  return (
                                  <td
                                      key={i}
                                      style={isLinoTeam ? { backgroundColor: '#254E97', color: 'white', fontWeight: 'bold' } : {}}
                                  >
                                      {value}
                                  </td>
                                  );
                              })}
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                    </div>
                </div>
              ))}
            </>
          ) : (
            <Alert variant="info">No fixtures found in the schedule.</Alert>
          )}
        </Tab>

      </Tabs>
    )}
  </Container>
  );
};

export default Schedule;
